import { motion } from "framer-motion";
import { ShieldCheck, Clock, Handshake, MapPin } from "lucide-react";
import { usePageText } from "@/hooks/useCms";

// Ícones fixos por posição (podem ser reordenados no admin mas mantêm-se estes)
const CARD_ICONS = [ShieldCheck, Clock, Handshake, MapPin];

const FALLBACK_CARDS = [
  { title: "Cumprimento legal", description: "Asseguramos o cumprimento dos requisitos técnicos e legais aplicáveis aos ativos abrangidos pela intervenção." },
  { title: "Redução do tempo administrativo", description: "Prestador único para todas as inspeções — deixa de gerir múltiplos fornecedores, prazos e certificados." },
  { title: "Interlocutor multidisciplinar", description: "Gás, elétrica, elevadores, equipamentos sob pressão, ambiente, energia e engenharia numa única entidade acreditada IPAC." },
  { title: "Cobertura nacional", description: "Mais de 150 técnicos em todo o território continental e regiões autónomas, com tempo médio de resposta de 24 horas." },
];

export default function Porque() {
  const { data } = usePageText("home-porque", {
    eyebrow: "Âmbito de intervenção",
    title: "Quatro áreas de valor.",
    subtitle: "Uma única entidade responsável.",
    intro_paragraphs: [],
    cards: FALLBACK_CARDS,
  });

  const eyebrow = data.eyebrow || "Âmbito de intervenção";
  const title = data.title || "Quatro áreas de valor.";
  const subtitle = data.subtitle || "";
  const introParagraphs = Array.isArray(data.intro_paragraphs) ? data.intro_paragraphs.filter(Boolean) : [];
  const cards = Array.isArray(data.cards) && data.cards.length ? data.cards : FALLBACK_CARDS;

  return (
    <section className="py-14 bg-[#F9FAFB] dark:bg-[#0D1320] border-y border-slate-200 dark:border-white/10" data-testid="porque-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-10">
          <p className="text-xs tracking-[0.24em] uppercase font-semibold text-[#1698AC] dark:text-[#4FC6D8] mb-4" data-testid="porque-eyebrow">
            {eyebrow}
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-medium leading-[1.1]" data-testid="porque-title">
            {title}
            {subtitle && (
              <>
                <br />
                {subtitle}
              </>
            )}
          </h2>
          {introParagraphs.length > 0 && (
            <div className="mt-6 text-slate-500 dark:text-slate-400 leading-relaxed max-w-xl space-y-1.5" data-testid="porque-intro">
              {introParagraphs.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((b, i) => {
            const Icon = CARD_ICONS[i % CARD_ICONS.length];
            const cardTitle = b.title || "";
            const cardDesc = b.description || b.text || "";
            return (
              <motion.div
                key={`${cardTitle}-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="bg-white dark:bg-[#0E1522] border border-slate-200 dark:border-white/10 rounded-lg p-7 hover:-translate-y-1 hover:border-[#1698AC]/40 dark:hover:border-[#4FC6D8]/40 hover:shadow-md transition-all duration-300"
                data-testid={`porque-card-${i}`}
              >
                <span className="inline-flex w-11 h-11 rounded-full bg-[#1698AC]/10 dark:bg-[#4FC6D8]/10 items-center justify-center text-[#1698AC] dark:text-[#4FC6D8] mb-5">
                  <Icon size={19} />
                </span>
                <h3 className="font-heading text-lg font-semibold mb-2 leading-snug">{cardTitle}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{cardDesc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
